
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { addDays, format, parse, isValid, addWeeks } from 'date-fns';
import { Calendar } from 'lucide-react';

const DueDateCalculator = () => {
  const [calculationType, setCalculationType] = useState('lmp');
  const [startDate, setStartDate] = useState('');
  const [ivfTransferDate, setIvfTransferDate] = useState('');
  const [ivfDayType, setIvfDayType] = useState('3');
  const [ultrasoundDate, setUltrasoundDate] = useState('');
  const [ultrasoundWeeks, setUltrasoundWeeks] = useState('');
  const [ultrasoundDays, setUltrasoundDays] = useState('');
  const [results, setResults] = useState<{
    dueDate?: string;
    conceptDate?: string;
    currentStage?: string;
    message?: string;
  }>({});

  const handleCalculate = () => {
    try {
      let dueDateObj;
      let conceptDateObj;

      switch (calculationType) {
        case 'lmp':
          // Calculate due date based on Last Menstrual Period
          const lmpDate = parse(startDate, 'yyyy-MM-dd', new Date());
          
          if (!isValid(lmpDate)) {
            setResults({ message: 'Please enter a valid LMP date' });
            return;
          }
          
          // Naegele's rule: LMP + 280 days (40 weeks)
          dueDateObj = addDays(lmpDate, 280);
          // Conception typically occurs ~14 days after LMP
          conceptDateObj = addDays(lmpDate, 14);
          break;
          
        case 'conception':
          // Calculate due date based on known conception date
          const conceptionDate = parse(startDate, 'yyyy-MM-dd', new Date());
          
          if (!isValid(conceptionDate)) {
            setResults({ message: 'Please enter a valid conception date' });
            return;
          }
          
          // Due date is conception date + 266 days (38 weeks)
          dueDateObj = addDays(conceptionDate, 266);
          conceptDateObj = conceptionDate;
          break;
          
        case 'ivf':
          // Calculate due date based on IVF transfer date
          const transferDate = parse(ivfTransferDate, 'yyyy-MM-dd', new Date());
          
          if (!isValid(transferDate)) {
            setResults({ message: 'Please enter a valid transfer date' });
            return;
          }
          
          // Days to add depends on the embryo age at transfer
          // 3-day transfer: 266 days - 3 days = 263 days from transfer to due date
          // 5-day transfer: 266 days - 5 days = 261 days from transfer to due date
          const daysToAdd = ivfDayType === '3' ? 263 : 261;
          dueDateObj = addDays(transferDate, daysToAdd);
          // Conception date is essentially the fertilization date in the lab
          conceptDateObj = addDays(transferDate, -(parseInt(ivfDayType)));
          break;
          
        case 'ultrasound':
          // Calculate due date based on ultrasound measurements
          const scanDate = parse(ultrasoundDate, 'yyyy-MM-dd', new Date());
          const weeksAtScan = parseInt(ultrasoundWeeks) || 0;
          const daysAtScan = parseInt(ultrasoundDays) || 0;
          
          if (!isValid(scanDate) || weeksAtScan < 0 || daysAtScan < 0) {
            setResults({ message: 'Please enter valid ultrasound information' });
            return;
          }
          
          // Calculate remaining weeks and days from ultrasound to due date
          const totalDaysAtScan = (weeksAtScan * 7) + daysAtScan;
          const remainingDays = 280 - totalDaysAtScan;
          
          dueDateObj = addDays(scanDate, remainingDays);
          // Approximate conception date (2 weeks after LMP equivalent)
          const lmpEquivalent = addDays(scanDate, -(totalDaysAtScan));
          conceptDateObj = addDays(lmpEquivalent, 14);
          break;
          
        default:
          setResults({ message: 'Please select a calculation method' });
          return;
      }
      
      // Determine current pregnancy stage
      const today = new Date();
      const totalPregnancyDays = 280;
      const daysUntilDue = Math.round((dueDateObj.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
      const daysPregnant = totalPregnancyDays - daysUntilDue;
      
      let currentStage = '';
      if (daysPregnant < 0) {
        currentStage = 'Not pregnant yet';
      } else if (daysPregnant <= 84) {  // First trimester (up to 12 weeks)
        const weeks = Math.floor(daysPregnant / 7);
        const days = daysPregnant % 7;
        currentStage = `First trimester: ${weeks} weeks and ${days} days pregnant`;
      } else if (daysPregnant <= 182) {  // Second trimester (up to 26 weeks)
        const weeks = Math.floor(daysPregnant / 7);
        const days = daysPregnant % 7;
        currentStage = `Second trimester: ${weeks} weeks and ${days} days pregnant`;
      } else if (daysPregnant <= 280) {  // Third trimester (up to 40 weeks)
        const weeks = Math.floor(daysPregnant / 7);
        const days = daysPregnant % 7;
        currentStage = `Third trimester: ${weeks} weeks and ${days} days pregnant`;
      } else {
        const daysOverdue = daysPregnant - 280;
        currentStage = `${daysOverdue} days past due date`;
      }
      
      setResults({
        dueDate: format(dueDateObj, 'MMMM d, yyyy'),
        conceptDate: format(conceptDateObj, 'MMMM d, yyyy'),
        currentStage: currentStage
      });
    } catch (error) {
      setResults({ message: 'Error calculating due date. Please check your inputs.' });
    }
  };

  return (
    <Card className="w-full">
      <CardContent className="pt-6">
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Due Date Calculator</h3>
            <p className="text-sm text-muted-foreground">
              Calculate your estimated due date using different methods.
            </p>
          </div>
          
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="calculationType">Calculation Method</Label>
              <Select 
                value={calculationType} 
                onValueChange={(value) => {
                  setCalculationType(value);
                  setStartDate(''); // Reset date when changing method
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select calculation method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lmp">Last Menstrual Period</SelectItem>
                  <SelectItem value="conception">Conception Date</SelectItem>
                  <SelectItem value="ivf">IVF Transfer Date</SelectItem>
                  <SelectItem value="ultrasound">Ultrasound Date</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Different input fields based on calculation type */}
            {calculationType === 'lmp' && (
              <div className="grid gap-2">
                <Label htmlFor="lmpDate">First Day of Last Menstrual Period</Label>
                <div className="flex">
                  <Input
                    id="lmpDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="flex-1"
                  />
                  <div className="ml-2 bg-muted w-10 flex items-center justify-center rounded-md">
                    <Calendar className="h-4 w-4" />
                  </div>
                </div>
              </div>
            )}

            {calculationType === 'conception' && (
              <div className="grid gap-2">
                <Label htmlFor="conceptionDate">Conception Date</Label>
                <div className="flex">
                  <Input
                    id="conceptionDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="flex-1"
                  />
                  <div className="ml-2 bg-muted w-10 flex items-center justify-center rounded-md">
                    <Calendar className="h-4 w-4" />
                  </div>
                </div>
              </div>
            )}

            {calculationType === 'ivf' && (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="ivfTransferDate">IVF Transfer Date</Label>
                  <div className="flex">
                    <Input
                      id="ivfTransferDate"
                      type="date"
                      value={ivfTransferDate}
                      onChange={(e) => setIvfTransferDate(e.target.value)}
                      className="flex-1"
                    />
                    <div className="ml-2 bg-muted w-10 flex items-center justify-center rounded-md">
                      <Calendar className="h-4 w-4" />
                    </div>
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ivfDayType">Embryo Age at Transfer</Label>
                  <Select value={ivfDayType} onValueChange={setIvfDayType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select embryo age" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3-Day Transfer</SelectItem>
                      <SelectItem value="5">5-Day Transfer (Blastocyst)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {calculationType === 'ultrasound' && (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="ultrasoundDate">Ultrasound Date</Label>
                  <div className="flex">
                    <Input
                      id="ultrasoundDate"
                      type="date"
                      value={ultrasoundDate}
                      onChange={(e) => setUltrasoundDate(e.target.value)}
                      className="flex-1"
                    />
                    <div className="ml-2 bg-muted w-10 flex items-center justify-center rounded-md">
                      <Calendar className="h-4 w-4" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-2">
                    <Label htmlFor="ultrasoundWeeks">Weeks</Label>
                    <Input
                      id="ultrasoundWeeks"
                      type="number"
                      min="0"
                      max="40"
                      value={ultrasoundWeeks}
                      onChange={(e) => setUltrasoundWeeks(e.target.value)}
                      placeholder="e.g., 8"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="ultrasoundDays">Days</Label>
                    <Input
                      id="ultrasoundDays"
                      type="number"
                      min="0"
                      max="6"
                      value={ultrasoundDays}
                      onChange={(e) => setUltrasoundDays(e.target.value)}
                      placeholder="e.g., 3"
                    />
                  </div>
                </div>
              </>
            )}
            
            <Button onClick={handleCalculate} className="mt-2">
              Calculate Due Date
            </Button>
          </div>

          {results.message ? (
            <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-md">
              {results.message}
            </div>
          ) : results.dueDate ? (
            <div className="mt-6 space-y-4">
              <div className="p-4 border rounded-md">
                <h4 className="text-lg font-medium mb-4">Due Date Information</h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium">Estimated Due Date:</p>
                    <p className="text-lg font-semibold text-primary">{results.dueDate}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Estimated Conception Date:</p>
                    <p>{results.conceptDate}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Current Stage:</p>
                    <p className="text-primary">{results.currentStage}</p>
                  </div>
                </div>
              </div>

              <div className="text-sm text-muted-foreground">
                <p>Note: Only about 5% of babies are born on their exact due date. Most babies are born within a window of about two weeks before or after the estimated due date.</p>
              </div>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export default DueDateCalculator;
